/**
 * IntakeWizardContext (V2)
 * ────────────────────────
 * Feb 2026 — Phases 1 & 2 of B2 IntakeWizard extraction.
 *
 * Owns the IntakeWizard state and renders the dialog inside the V2 route
 * tree, so V2 callers (e.g. WaitlistRoute's Edit pencil, "Add to
 * Waitlist") can open the dialog NATIVELY instead of bouncing to
 * /dashboard.
 *
 * Phase 1 scope:
 *   ✅ Edit an existing client (load + edit + save + close)
 *   ✅ Save draft for an existing client
 *
 * Phase 2 scope (Feb 2026):
 *   ✅ New-client creation (build clientData + duplicate-check +
 *      DuplicateWarningDialog + cascade of contacts/enrollments/
 *      insurances/medications)
 *   ✅ Real permission helpers via `useIntakePermissions(intakeFormConfig)`
 *   ✅ intakeFormConfig fetched once at provider mount
 *
 * Still bounce-to-legacy in Phase 2:
 *   ❌ Change-request dialog
 *   ❌ Provider-assignment dialog
 *   ❌ Medication editor dialog
 *   ❌ PDF generation
 *   ❌ Change-history viewer
 *
 * Legacy /dashboard is UNAFFECTED — App.js still owns its own copy of
 * the state and dialog mount.  Two owners is intentional through Phase
 * 2 (Phase 3 collapses them into one).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import IntakeWizardDialog from '../components/features/IntakeWizardDialog';
import DuplicateWarningDialog from '../components/features/DuplicateWarningDialog';
import ChangeRequestDialog from '../components/features/ChangeRequestDialog';
import AssignProvidersDialog from '../components/features/AssignProvidersDialog';
import { AddEditMedicationDialog } from '../components/features/AddEditMedicationDialog';
import { ClientInsuranceDialog } from '../components/features/ClientInsuranceDialog';
import { ChangeHistoryModal } from '../components/features/ChangeHistoryModal';
import { useAuth } from '../context/AuthContext';
import { useStaticData } from '../context/StaticDataContext';
import { useClients } from '../context/ClientsContext';
import { useIntakePermissions } from '../hooks/useIntakePermissions';
import { validateIntakeRequiredFields } from '../utils/intakeValidators';
import { generateIntakePdf } from '../utils/intakePdfGenerator';
import { useEditingClient } from './useEditingClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IntakeWizardContext = createContext(null);

const noop = () => {};

// Parse a full_name string back into first/middle/last for the form.
const parseFullName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return { first: '', middle: '', last: '' };
  }
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], middle: '', last: '' };
  if (parts.length === 2) return { first: parts[0], middle: '', last: parts[1] };
  return {
    first: parts[0],
    middle: parts.slice(1, -1).join(' '),
    last: parts[parts.length - 1],
  };
};

// Map a fetched client document into the flat field-shape the dialog
// expects.  Mirrors App.js's `setIntakeData({...})` block in
// handleOpenIntakeWizard.
const clientToIntakeData = (client) => {
  let firstName = client.first_name || '';
  let middleName = client.middle_name || '';
  let lastName = client.last_name || '';
  if (!firstName && !lastName && client.full_name) {
    const parsed = parseFullName(client.full_name);
    firstName = parsed.first;
    middleName = parsed.middle;
    lastName = parsed.last;
  }
  const mapped = {
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    preferred_name: client.preferred_name || '',
    full_name: client.full_name || '',
    date_of_birth: client.date_of_birth || '',
    ssn: client.ssn || '',
    gender: client.gender || '',
    marital_status: client.marital_status || '',
    status: client.status || 'pending_intake',
    occupation: client.occupation || '',
    race: client.race || '',
    ethnicity: client.ethnicity || '',
    language: client.language || '',
    language_other: client.language_other || '',
    address_line1: client.address_line1 || '',
    address_line2: client.address_line2 || '',
    city: client.city || '',
    state: client.state || '',
    zipcode: client.zipcode || '',
    use_separate_insurance_address: client.use_separate_insurance_address || false,
    insurance_address_line1: client.insurance_address_line1 || '',
    insurance_address_line2: client.insurance_address_line2 || '',
    insurance_city: client.insurance_city || '',
    insurance_state: client.insurance_state || '',
    insurance_zipcode: client.insurance_zipcode || '',
    phone: client.phone || '',
    phone_secondary: client.phone_secondary || '',
    email: client.email || '',
    has_guardian: client.has_guardian || false,
    is_minor_or_guardian_present: client.is_minor_or_guardian_present || false,
    guardian_name: client.guardian_name || '',
    guardian_contact: client.guardian_contact || '',
    guardian_phone: client.guardian_phone || '',
    guardian_email: client.guardian_email || '',
    guardian_same_household: client.guardian_same_household || false,
    referral_source: client.referral_source || '',
    referral_contact: client.referral_contact || '',
    referral_organization: client.referral_organization || '',
    referral_phone: client.referral_phone || '',
    referral_email: client.referral_email || '',
    referral_type: client.referral_type || '',
    reason_for_referral: client.reason_for_referral || '',
    symptoms_checklist: client.symptoms_checklist || [],
    service_requested: client.service_requested || '',
    preferred_mode: client.preferred_mode || '',
    preferred_location: client.preferred_location || '',
    preferred_provider_type: client.preferred_provider_type || '',
    ok_to_call_times: client.ok_to_call_times || '',
    pets_in_home: client.pets_in_home || '',
    pets_description: client.pets_description || '',
    has_pcp: client.has_pcp || '',
    pcp_name: client.pcp_name || '',
    pcp_phone: client.pcp_phone || '',
    pcp_address: client.pcp_address || '',
    last_physical_date: client.last_physical_date || '',
    medical_alerts: client.medical_alerts || '',
    allergies: client.allergies || '',
    interpreter_needed: client.interpreter_needed || false,
    requesting_substance_services: client.requesting_substance_services || '',
    requires_24q_assessment: client.requires_24q_assessment || '',
    assessment_date: client.assessment_date || '',
    recorded_bac: client.recorded_bac || '',
    pharmacy_name: client.pharmacy_name || '',
    pharmacy_phone: client.pharmacy_phone || '',
    pharmacy_fax: client.pharmacy_fax || '',
    pharmacy_address: client.pharmacy_address || '',
    payment_type:
      ((client.insurances || []).filter((i) => i.status === 'active').length > 0)
        ? 'has_insurance'
        : 'self_pay',
    primary_insurance: client.primary_insurance || '',
    primary_member_id: client.primary_member_id || '',
    primary_group_number: client.primary_group_number || '',
    primary_relationship_to_insured: client.primary_relationship_to_insured || '',
    primary_insured_name: client.primary_insured_name || '',
    primary_insured_ssn: client.primary_insured_ssn || '',
    primary_insured_dob: client.primary_insured_dob || '',
    primary_card_front: client.primary_card_front || '',
    primary_card_back: client.primary_card_back || '',
    has_secondary_insurance: !!client.secondary_insurance,
    secondary_insurance: client.secondary_insurance || '',
    secondary_member_id: client.secondary_member_id || '',
    secondary_group_number: client.secondary_group_number || '',
    secondary_relationship_to_insured: client.secondary_relationship_to_insured || '',
    secondary_insured_name: client.secondary_insured_name || '',
    secondary_insured_ssn: client.secondary_insured_ssn || '',
    secondary_insured_dob: client.secondary_insured_dob || '',
    secondary_card_front: client.secondary_card_front || '',
    secondary_card_back: client.secondary_card_back || '',
    has_tertiary_insurance: !!client.tertiary_insurance,
    tertiary_insurance: client.tertiary_insurance || '',
    tertiary_member_id: client.tertiary_member_id || '',
    tertiary_group_number: client.tertiary_group_number || '',
    tertiary_relationship_to_insured: client.tertiary_relationship_to_insured || '',
    tertiary_insured_name: client.tertiary_insured_name || '',
    tertiary_insured_ssn: client.tertiary_insured_ssn || '',
    tertiary_insured_dob: client.tertiary_insured_dob || '',
    tertiary_card_front: client.tertiary_card_front || '',
    tertiary_card_back: client.tertiary_card_back || '',
    emergency_contact_1_name: client.emergency_contacts?.[0]?.name || '',
    emergency_contact_1_relationship: client.emergency_contacts?.[0]?.relationship || '',
    emergency_contact_1_relationship_other: '',
    emergency_contact_1_phone: client.emergency_contacts?.[0]?.phone || '',
    emergency_contact_1_email: client.emergency_contacts?.[0]?.email || '',
    emergency_contact_2_name: client.emergency_contacts?.[1]?.name || '',
    emergency_contact_2_relationship: client.emergency_contacts?.[1]?.relationship || '',
    emergency_contact_2_relationship_other: '',
    emergency_contact_2_phone: client.emergency_contacts?.[1]?.phone || '',
    emergency_contact_2_email: client.emergency_contacts?.[1]?.email || '',
    assigned_providers: client.assigned_providers || [],
  };
  // Merge any unsaved draft on top of the persisted snapshot so reopening
  // the wizard shows the user's in-progress edits.  Without this, clicking
  // "Save Draft" → close → reopen would appear to wipe the data because
  // `intake_draft.data` is stored on the client doc but never re-read.
  const draft = client?.intake_draft?.data;
  if (draft && typeof draft === 'object') {
    return { ...mapped, ...draft };
  }
  return mapped;
};

export function IntakeWizardProvider({ children, onSaved }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    locations,
    users,
    providers,
    serviceTypes,
    brandingSettings,
    insurances: insuranceCompanies,
    setProviders: setStaticDataProviders,
  } = useStaticData();
  // Mar 2026 — port the real PDF generator into V2.  Previously the
  // V2 dialog just toasted "only available in legacy dashboard for
  // now" and tried to navigate there, but every "Edit Client" /
  // "Add Client" entry point in legacy /dashboard now also opens the
  // V2 dialog (via `intakeWizard.open(...)`), so even users sitting
  // on /dashboard hit the toast.  Generating the PDF here gives us
  // one implementation that works regardless of route.
  const clientsCtx = useClients?.() || {};
  const setClientDocuments = clientsCtx.setClientDocuments;
  const selectedClientFromCtx = clientsCtx.selectedClient;

  // ── State ────────────────────────────────────────────────────────
  const [showIntakeWizard, setShowIntakeWizard] = useState(false);
  const [intakeClientId, setIntakeClientId] = useState(null);
  const [intakeData, setIntakeData] = useState({});
  const [intakeLastSaved, setIntakeLastSaved] = useState(null);
  const [intakeAutoSaving, setIntakeAutoSaving] = useState(false);
  const [intakeValidationErrors, setIntakeValidationErrors] = useState({});
  const [editingClient, setEditingClient, safeSetEditingClient] = useEditingClient(null);
  const [pendingInsurances, setPendingInsurances] = useState([]);
  const [realtimeDuplicateWarnings, setRealtimeDuplicateWarnings] = useState([]);
  const [checkingRealtimeDuplicates, setCheckingRealtimeDuplicates] = useState(false);

  // Phase 2 — new-client creation state
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [intakePotentialDuplicates, setIntakePotentialDuplicates] = useState([]);
  const [pendingClientData, setPendingClientData] = useState(null);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [revealedSSNs, setRevealedSSNs] = useState({});
  // Phase 3e.0 — surface a loading indicator while the duplicate-check
  // API call is in flight (matches App.js's setCheckingDuplicates at
  // line 15543).  Not currently rendered by IntakeWizardDialog but
  // exposed for future UI parity.
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  // Phase 3b — change-request state.  When the user clicks "Request
  // Change" inside the IntakeWizard for a field/section they can't
  // edit directly, the wizard calls `setShowChangeRequestDialog(true)`
  // after setting `changeRequestSection`.  We render ChangeRequestDialog
  // natively so V2 no longer needs to bounce to legacy for this flow.
  const [showChangeRequestDialog, setShowChangeRequestDialog] = useState(false);
  const [changeRequestSection, setChangeRequestSection] = useState(null);
  const [changeRequestReason, setChangeRequestReason] = useState('');

  // March 2026 P0 — the IntakeWizardDialog's "Assign Services" button
  // (and several other handlers in the extracted dialog) calls
  // `setSelectedClient(...)` as a tracking shim left over from the
  // legacy App.js monolith.  Mapping it to `setEditingClient` is FATAL
  // for the new-client flow:
  //   1. New client → "Assign Services" sets `selectedClient = {id:'new'}`.
  //   2. The shim flipped `editingClient` to `{id:'new', ...}`.
  //   3. IntakeWizardDialog's seed-formData useEffect (keyed on
  //      `editingClient?.id`) re-fired and wiped every typed field
  //      because `intakeData` was still empty (user hadn't clicked
  //      Save Draft yet).
  //   4. Dialog title flipped to "Update Client" (editingClient truthy).
  //   5. Submit → PUT /clients/new → 404 "Client not found".
  // Keep `selectedClient` as a SEPARATE state.  We only promote it to
  // `editingClient` after a successful enrollment-create on a REAL
  // client (see the AssignProvidersDialog `setSelectedClient` wiring
  // below for the gating logic).
  const [selectedClientForAssign, setSelectedClientForAssign] = useState(null);

  // Phase 3c.2 — provider/service assignment state.  The wizard owns
  // its own data-fetching for providers/services/enrollments and
  // mutates these states via the setters; we just need to hold the
  // values and render AssignProvidersDialog inline.
  const [showAssignProviders, setShowAssignProviders] = useState(false);
  const [selectedProvidersForAssignment, setSelectedProvidersForAssignment] = useState([]);
  const [providerServiceAssignments, setProviderServiceAssignments] = useState({});
  const [availableServices, setAvailableServices] = useState([]);
  const [existingEnrollments, setExistingEnrollments] = useState([]);

  // Phase 3d.2 — medication editor state.  The wizard calls
  // `setEditingMedication(med)` first (or `(null)` for "add new"),
  // then `setShowMedicationForm(true)`.  We render
  // AddEditMedicationDialog inline; on success we refresh
  // editingClient.medications + sync intakeData.medications.
  const [showMedicationForm, setShowMedicationForm] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);

  // Intake form config (admin form-builder), fetched once on mount.
  // Drives `isFieldVisible` / `isFieldRequired`.  Best-effort: if the
  // endpoint 404s for non-admin users, we fall back to permissive
  // defaults (every field visible, none required).
  const [intakeFormConfig, setIntakeFormConfig] = useState(null);
  useEffect(() => {
    // Phase 3e.1 — guard the fetch on having an authenticated user.
    // Before 3e.1 the provider was only mounted inside
    // DashboardLayoutV2 (which is itself wrapped in ProtectedRoute),
    // so it was guaranteed to mount only after login.  Now that the
    // provider lives at App.js root, it mounts before/regardless of
    // login state — gate the fetch so we don't 401 on /login or /portal.
    if (!user?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/config/intake-form`);
        if (!cancelled) setIntakeFormConfig(res.data);
      } catch (_) {
        // 401/403/404 are fine — we just won't gate any fields.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const permissions = useIntakePermissions(intakeFormConfig);

  // ── open / close ─────────────────────────────────────────────────
  const close = useCallback(() => {
    setShowIntakeWizard(false);
    setIntakeClientId(null);
    setIntakeData({});
    setIntakeLastSaved(null);
    setEditingClient(null);
    setRealtimeDuplicateWarnings([]);
    setPendingInsurances([]);
    setIntakeValidationErrors({});
    // Phase 2 — clear new-client state too
    setShowDuplicateWarning(false);
    setIntakePotentialDuplicates([]);
    setPendingClientData(null);
    setRevealedSSNs({});
    // Phase 3b — clear change-request state too
    setShowChangeRequestDialog(false);
    setChangeRequestSection(null);
    setChangeRequestReason('');
    // Phase 3c.2 — clear assign-providers state too
    setShowAssignProviders(false);
    setSelectedProvidersForAssignment([]);
    setProviderServiceAssignments({});
    setAvailableServices([]);
    setExistingEnrollments([]);
    // Phase 3d.2 — clear medication-form state too
    setShowMedicationForm(false);
    setEditingMedication(null);
    // March 2026 P0 — clear the AssignProviders tracking shim too so
    // the next wizard open starts clean.
    setSelectedClientForAssign(null);
  }, []);

  const open = useCallback(async (clientId, defaultStatus = null) => {
    if (!clientId) {
      // Phase 2: native new-client creation.  Reset state with the
      // requested default status (e.g. 'waitlist' for "Add to
      // Waitlist" button) and open an empty form.
      setIntakeData(defaultStatus ? { status: defaultStatus } : {});
      setEditingClient(null);
      setIntakeClientId(null);
      setIntakeLastSaved(null);
      setIntakeValidationErrors({});
      setRealtimeDuplicateWarnings([]);
      setPendingInsurances([]);
      setShowIntakeWizard(true);
      return;
    }
    try {
      const response = await axios.get(`${API}/clients/${clientId}`);
      const client = response.data;
      let medications = [];
      try {
        const meds = await axios.get(`${API}/clients/${clientId}/medications`);
        medications = meds.data || [];
      } catch (_) {
        // medications fetch is best-effort
      }
      setIntakeData(clientToIntakeData(client));
      setEditingClient({ ...client, medications });
      setIntakeClientId(clientId);
      // Surface the persisted draft's last-saved timestamp so the dialog's
      // "Last saved …" badge reflects reality on reopen.
      setIntakeLastSaved(client?.intake_last_saved ? new Date(client.intake_last_saved) : null);
      setShowIntakeWizard(true);
    } catch (err) {
      console.error('IntakeWizard open() failed:', err);
      toast.error('Failed to load client data');
    }
  }, []);

  // ── Field change (mirrors App.js handleIntakeFieldChange) ────────
  const handleIntakeFieldChange = useCallback((field, value) => {
    setIntakeData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'first_name' || field === 'middle_name' || field === 'last_name') {
        const f = field === 'first_name' ? value : (prev.first_name || '');
        const m = field === 'middle_name' ? value : (prev.middle_name || '');
        const l = field === 'last_name' ? value : (prev.last_name || '');
        next.full_name = `${f} ${m} ${l}`.trim().replace(/\s+/g, ' ');
      }
      return next;
    });
  }, []);

  const handleIntakeFieldValidation = useCallback((fieldName, errorMessage) => {
    setIntakeValidationErrors((prev) => {
      const next = { ...prev };
      if (errorMessage) {
        next[fieldName] = errorMessage;
      } else {
        delete next[fieldName];
      }
      return next;
    });
  }, []);

  // ── Save draft — supports both new-client and existing-client. ──
  // For new clients (no intakeClientId yet), creates a stub client
  // with pending_intake status first, mirroring App.js behavior at
  // line 15047.
  const handleSaveIntakeDraft = useCallback(async (dataOverride = null) => {
    const data = dataOverride && typeof dataOverride === 'object'
      ? dataOverride
      : intakeData;
    try {
      let targetId = intakeClientId;
      if (!targetId) {
        // Create stub client first
        const stub = await axios.post(`${API}/clients`, {
          full_name: data.full_name || 'Pending',
          first_name: data.first_name || '',
          middle_name: data.middle_name || '',
          last_name: data.last_name || '',
          status: 'pending_intake',
          created_by: user?.id,
        });
        targetId = stub.data.id;
        setIntakeClientId(targetId);
      }
      await axios.post(`${API}/clients/${targetId}/intake-draft`, {
        current_step: 1,
        data,
      });
      setIntakeLastSaved(new Date());
      setIntakeData(data);
      toast.success('Draft saved successfully');
    } catch (_) {
      toast.error('Failed to save draft');
    }
  }, [intakeClientId, intakeData, user]);

  // ── Complete intake — supports BOTH edit-existing and new-client. ─
  // Mirrors App.js handleCompleteIntake at the structural level.
  // Build the clientData payload used by both create + update.
  // Mirrors the App.js logic at lines 15416–15479: derive emergency
  // contacts array, strip flat keys, ensure assigned_providers.
  const buildClientPayload = useCallback((data) => {
    const clientData = {
      ...data,
      status: data.status || 'active',
      intake_completed: true,
    };
    if (!clientData.assigned_providers) clientData.assigned_providers = [];

    const emergencyContacts = [];
    if (data.emergency_contact_1_name) {
      emergencyContacts.push({
        name: data.emergency_contact_1_name,
        relationship: data.emergency_contact_1_relationship === 'Other'
          ? (data.emergency_contact_1_relationship_other || 'Other')
          : (data.emergency_contact_1_relationship || ''),
        phone: data.emergency_contact_1_phone || null,
        email: data.emergency_contact_1_email || null,
        can_receive_info: false,
        release_on_file: false,
      });
    }
    if (data.emergency_contact_2_name) {
      emergencyContacts.push({
        name: data.emergency_contact_2_name,
        relationship: data.emergency_contact_2_relationship === 'Other'
          ? (data.emergency_contact_2_relationship_other || 'Other')
          : (data.emergency_contact_2_relationship || ''),
        phone: data.emergency_contact_2_phone || null,
        email: data.emergency_contact_2_email || null,
        can_receive_info: false,
        release_on_file: false,
      });
    }
    if (emergencyContacts.length > 0) clientData.emergency_contacts = emergencyContacts;

    [
      'emergency_contact_1_name', 'emergency_contact_1_relationship',
      'emergency_contact_1_relationship_other', 'emergency_contact_1_phone',
      'emergency_contact_1_email', 'emergency_contact_2_name',
      'emergency_contact_2_relationship', 'emergency_contact_2_relationship_other',
      'emergency_contact_2_phone', 'emergency_contact_2_email',
      'medications',
    ].forEach((k) => delete clientData[k]);

    return clientData;
  }, []);

  // After successful client creation, fire-and-forget the cascading
  // side-effects (guardian contact, insurances, medications).  Each
  // failure is logged + toasted but doesn't block the success path —
  // matches App.js behavior at lines 15623–15741.
  const cascadeNewClientSideEffects = useCallback(async (clientId, data) => {
    // Guardian contact (new-client path — straight POST, no dedup needed)
    if (data.has_guardian && data.guardian_name) {
      try {
        const guardianContact = {
          name: data.guardian_name,
          relationship: 'parent',
          phone: data.guardian_phone || null,
          email: data.guardian_email || null,
          can_receive_info: false,
          release_on_file: false,
          is_minor_or_legal_guardian: true,
          lives_in_same_household: data.guardian_same_household || false,
        };
        await axios.post(`${API}/clients/${clientId}/contacts`, guardianContact);
        if (data.guardian_is_emergency_contact) {
          await axios.post(`${API}/clients/${clientId}/emergency-contacts`, {
            name: data.guardian_name,
            relationship: 'Parent/Guardian',
            phone: data.guardian_phone || null,
            email: data.guardian_email || null,
          });
        }
      } catch (err) {
        console.error('Guardian contact cascade failed:', err);
      }
    }

    // Pending service enrollments — Phase 3e.0 parity.  Matches
    // App.js:15633 — reads from data.pending_service_enrollments
    // (staged by AssignProvidersDialog in the new-client branch).
    const pendingEnrollments = data.pending_service_enrollments;
    if (pendingEnrollments && Object.keys(pendingEnrollments).length > 0) {
      for (const providerId of Object.keys(pendingEnrollments)) {
        const services = pendingEnrollments[providerId] || [];
        for (const serviceId of services) {
          try {
            await axios.post(
              `${API}/clients/${clientId}/service-enrollments`,
              { provider_id: providerId, service_type: serviceId, visit_frequency: 'weekly' },
              { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } },
            );
          } catch (enrollmentError) {
            if (enrollmentError.response?.status === 400
                || enrollmentError.response?.status === 409) {
              // Duplicate enrollment — silently skip (matches App.js:15651)
            } else {
              console.error('Failed to create service enrollment:', enrollmentError);
            }
          }
        }
      }
    }

    // Pending insurances
    if (pendingInsurances && pendingInsurances.length > 0) {
      for (const insurance of pendingInsurances) {
        try {
          await axios.post(`${API}/clients/${clientId}/insurances`, insurance);
        } catch (err) {
          console.error('Insurance cascade failed:', err);
          toast.warning(`Failed to add insurance: ${insurance.company}. You can add it from the client's profile.`);
        }
      }
    }

    // Inline medications from intakeData (rare — typically meds are
    // managed via the Medication dialog, but the dialog can stage them)
    if (data.medications && data.medications.length > 0) {
      for (const medication of data.medications) {
        if (medication.name) {
          try {
            await axios.post(`${API}/clients/${clientId}/medications`, {
              name: medication.name,
              dosage: medication.dosage || '',
              frequency: medication.frequency || '',
              prescriber: medication.prescriber || '',
              is_active: true,
              start_date: new Date().toISOString().split('T')[0],
            });
          } catch (err) {
            console.error('Medication cascade failed:', err);
          }
        }
      }
    }
  }, [pendingInsurances]);

  // ── Edit-branch guardian-contact upsert ─────────────────────────
  // Phase 3e.0 parity.  Mirrors App.js:15464–15531 — fetch existing
  // contacts, find a parent/guardian by name, PUT to update or POST
  // to create.  Then dedup the same person as an emergency contact.
  const upsertGuardianForExistingClient = useCallback(async (clientId, data) => {
    if (!data?.has_guardian || !data?.guardian_name) return;
    try {
      const guardianContact = {
        name: data.guardian_name,
        relationship: 'parent',
        phone: data.guardian_phone || null,
        email: data.guardian_email || null,
        can_receive_info: false,
        release_on_file: false,
        is_minor_or_legal_guardian: true,
        lives_in_same_household: data.guardian_same_household || false,
      };

      const existingContactsRes = await axios.get(`${API}/clients/${clientId}/contacts`);
      const existingContacts = existingContactsRes.data || [];
      const existingGuardian = existingContacts.find((c) => (
        c.name === data.guardian_name
        && (c.relationship === 'parent' || c.relationship === 'guardian')
      ));
      if (existingGuardian) {
        await axios.put(`${API}/contacts/${existingGuardian.id}`, guardianContact);
      } else {
        await axios.post(`${API}/clients/${clientId}/contacts`, guardianContact);
      }

      if (data.guardian_is_emergency_contact) {
        const emergencyContact = {
          name: data.guardian_name,
          relationship: 'parent',
          phone: data.guardian_phone || null,
          email: data.guardian_email || null,
        };
        const existingEmergencyRes = await axios.get(`${API}/clients/${clientId}/emergency-contacts`);
        const existingEmergency = (existingEmergencyRes.data || []).find((c) => (
          c.name === data.guardian_name
          && (c.relationship === 'parent' || c.relationship === 'guardian')
        ));
        if (!existingEmergency) {
          await axios.post(`${API}/clients/${clientId}/emergency-contacts`, emergencyContact);
        }
      }
    } catch (err) {
      // Match legacy: don't fail the whole save if contact ops fail.
      console.error('Failed to upsert guardian contact:', err);
    }
  }, []);

  // ── Complete intake — supports BOTH edit-existing and new-client. ─
  // Mirrors App.js handleCompleteIntake (line 15356) at the structural
  // level: build payload → if editing PUT and refresh, if new POST,
  // duplicate-check first.
  const handleCompleteIntake = useCallback(async (dataOverride = null) => {
    if (intakeAutoSaving) return;
    const data = (dataOverride && typeof dataOverride === 'object') ? dataOverride : intakeData;
    if (dataOverride && typeof dataOverride === 'object') {
      setIntakeData(dataOverride);
    }

    // Phase 3e.0 parity — validate the 5 mandatory fields before
    // submitting (matches App.js:15342–15378).  Surface errors via
    // intakeValidationErrors so the dialog highlights the offending
    // fields, and abort the submit.
    const validationErrors = validateIntakeRequiredFields(data);
    if (Object.keys(validationErrors).length > 0) {
      setIntakeValidationErrors(validationErrors);
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setIntakeAutoSaving(true);
    try {
      const clientData = buildClientPayload(data);

      if (editingClient) {
        // ── Edit existing client ───────────────────────────────────
        // Jun 2026 — allergies are managed by the structured
        // AllergiesSection (single source of truth); the backend derives
        // the client.allergies summary. Never send the stale free text
        // seeded when the wizard opened or it overwrites the summary.
        delete clientData.allergies;
        await axios.put(`${API}/clients/${editingClient.id}`, clientData);
        // Phase 3e.0 parity — upsert the guardian-as-parent contact
        // (and optional guardian-as-emergency-contact dedup) for the
        // existing client.  Matches App.js:15464–15531.
        await upsertGuardianForExistingClient(editingClient.id, data);
        toast.success('Client updated successfully!');
        close();
        if (typeof onSaved === 'function') onSaved(editingClient.id);
        return;
      }

      // ── New client: duplicate-check first ──────────────────────────
      setCheckingDuplicates(true);
      try {
        const params = new URLSearchParams();
        if (clientData.full_name) params.append('full_name', clientData.full_name);
        if (clientData.date_of_birth) params.append('date_of_birth', clientData.date_of_birth);
        if (clientData.ssn) params.append('ssn', clientData.ssn);
        if (clientData.email) params.append('email', clientData.email);
        if (clientData.phone) params.append('phone', clientData.phone);
        const dup = await axios.post(
          `${API}/clients/check-duplicates?${params.toString()}`,
          {},
          { timeout: 30000 },
        );
        setCheckingDuplicates(false);
        if (dup.data?.has_duplicates) {
          setIntakePotentialDuplicates(dup.data.duplicates || []);
          setPendingClientData(clientData);
          setShowDuplicateWarning(true);
          return;
        }
      } catch (dupErr) {
        setCheckingDuplicates(false);
        console.error('Duplicate check failed:', dupErr);
        toast.warning('Unable to check for duplicates, proceeding with client creation.');
      }

      // No duplicates — create the client
      const response = await axios.post(`${API}/clients`, clientData);
      const newId = response.data.id;
      await cascadeNewClientSideEffects(newId, data);

      toast.success(
        data.status === 'waitlist'
          ? 'Client added to waitlist successfully!'
          : 'Client intake completed successfully!',
      );
      close();
      if (typeof onSaved === 'function') onSaved(newId);
    } catch (err) {
      console.error('IntakeWizard handleCompleteIntake failed:', err);
      // Match App.js's pydantic-error humanization
      let errorMessage = editingClient ? 'Failed to update client' : 'Failed to create client';
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        errorMessage = detail.map((e) => {
          const field = Array.isArray(e.loc) && e.loc.length > 0 ? e.loc[e.loc.length - 1] : null;
          const label = field ? String(field).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null;
          return label ? `${label}: ${e.msg}` : e.msg;
        }).join('; ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    } finally {
      setIntakeAutoSaving(false);
    }
  }, [
    intakeAutoSaving,
    intakeData,
    editingClient,
    close,
    onSaved,
    buildClientPayload,
    cascadeNewClientSideEffects,
    upsertGuardianForExistingClient,
  ]);

  // ── Create-client-anyway (user clicked through duplicate warning) ─
  const handleCreateClientAnyway = useCallback(async () => {
    if (isCreatingClient || !pendingClientData) return;
    setIsCreatingClient(true);
    try {
      const response = await axios.post(`${API}/clients`, pendingClientData);
      const newId = response.data.id;
      // Re-use the cascade with the original intakeData (which still
      // holds the pre-flatten fields like guardian_*, medications, etc.)
      await cascadeNewClientSideEffects(newId, intakeData);
      toast.success('Client created successfully!');
      setShowDuplicateWarning(false);
      setPendingClientData(null);
      setIntakePotentialDuplicates([]);
      setRevealedSSNs({});
      close();
      if (typeof onSaved === 'function') onSaved(newId);
    } catch (err) {
      console.error('handleCreateClientAnyway failed:', err);
      const detail = err?.response?.data?.detail;
      let msg = 'Failed to create client';
      if (Array.isArray(detail)) msg = detail.map((e) => e.msg).join(', ');
      else if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object') msg = detail.msg || JSON.stringify(detail);
      toast.error(msg);
    } finally {
      setIsCreatingClient(false);
    }
  }, [isCreatingClient, pendingClientData, intakeData, cascadeNewClientSideEffects, close, onSaved]);

  // ── PDF generation: real implementation ──────────────────────────
  // Mar 2026 — was a stub that toasted "only available in legacy
  // dashboard for now" and navigated to /dashboard.  But every "Edit
  // Client" / "Add Client" button in legacy /dashboard now opens the
  // V2 dialog via `intakeWizard.open(...)` (Phase 3e.2/3a), so even
  // legacy users hit the V2 stub — manifesting as the confusing
  // toast "only available in legacy" while the user *is* on legacy.
  // Mirrors `handleGenerateIntakePDF` in App.js (~L15209).
  const handleGenerateIntakePDF = useCallback(async (dataOverride = null) => {
    const data = (dataOverride && typeof dataOverride === 'object') ? dataOverride : intakeData;
    if (dataOverride && typeof dataOverride === 'object') {
      setIntakeData(dataOverride);
    }
    try {
      // intakeFormConfig is already fetched at provider mount, but
      // re-fetch defensively if the user opened the wizard before the
      // initial fetch completed.
      let currentIntakeFormConfig = intakeFormConfig;
      if (!currentIntakeFormConfig || !currentIntakeFormConfig.steps) {
        try {
          const configResp = await axios.get(`${API}/config/intake-form`);
          if (configResp.data) {
            setIntakeFormConfig(configResp.data);
            currentIntakeFormConfig = configResp.data;
          }
        } catch (e) {
          console.error('[PDF] Failed to fetch intake form config:', e);
        }
      }

      const { blob: pdfBlob, fileName, currentDate } = await generateIntakePdf({
        intakeData: data,
        editingClient,
        intakeFormConfig: currentIntakeFormConfig,
        locations,
        brandingSettings,
      });

      const clientId = editingClient?.id || intakeClientId;
      if (!clientId) {
        toast.error('Please save the client first before generating PDF');
        return;
      }

      const formData = new FormData();
      formData.append('file', pdfBlob, fileName);
      formData.append('description', `Referral Form (${currentDate})`);
      formData.append('form_type', 'Intake Paperwork');

      await axios.post(`${API}/clients/${clientId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(`PDF generated and saved to Documents: ${fileName}`);

      // Refresh the documents tab if the user is viewing this client.
      if (selectedClientFromCtx && selectedClientFromCtx.id === clientId && setClientDocuments) {
        try {
          const documentsResponse = await axios.get(`${API}/clients/${clientId}/documents`);
          setClientDocuments(documentsResponse.data);
        } catch (e) {
          console.warn('[PDF] Failed to refresh client documents after PDF upload:', e);
        }
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF: ' + (error.response?.data?.detail || error.message));
    }
  }, [
    intakeData,
    setIntakeData,
    intakeFormConfig,
    setIntakeFormConfig,
    editingClient,
    intakeClientId,
    locations,
    brandingSettings,
    selectedClientFromCtx,
    setClientDocuments,
  ]);

  // ── Native client Change History (Phase C, Sep 2026) ─────────────
  // Previously bounced to legacy /dashboard; ChangeHistoryModal is
  // self-contained so it mounts natively under this provider.
  const [changeHistoryClient, setChangeHistoryClient] = useState(null);

  const handleOpenInsuranceManagement = useCallback((client) => {
    // Feb 2026 — Native V2 insurance management.  Previously this
    // bounced to legacy /dashboard, but the bounce never actually
    // triggered the legacy dialog state — the user just saw the
    // route flicker and the dialog never opened.  ClientInsuranceDialog
    // is a self-contained component, so we mount it directly under
    // this provider instead.
    const target = client || editingClient;
    if (!target?.id) {
      toast.error('Open the client first before managing insurance.');
      return;
    }
    setManagingInsuranceForClient(target);
    setShowInsuranceManagement(true);
  }, [editingClient]);

  // Native V2 insurance management state — used by
  // ClientInsuranceDialog mounted at the bottom of this provider.
  const [showInsuranceManagement, setShowInsuranceManagement] = useState(false);
  const [managingInsuranceForClient, setManagingInsuranceForClient] = useState(null);

  const ctxValue = useMemo(() => ({
    showIntakeWizard,
    intakeClientId,
    intakeData,
    editingClient,
    open,
    close,
  }), [showIntakeWizard, intakeClientId, intakeData, editingClient, open, close]);

  return (
    <IntakeWizardContext.Provider value={ctxValue}>
      {children}
      <IntakeWizardDialog
        // Dialog state
        showIntakeWizard={showIntakeWizard}
        setShowIntakeWizard={(next) => { if (!next) close(); else setShowIntakeWizard(true); }}

        // Client data
        editingClient={editingClient}
        intakeData={intakeData}
        intakeClientId={intakeClientId}
        intakeLastSaved={intakeLastSaved}
        intakeAutoSaving={intakeAutoSaving}
        intakeValidationErrors={intakeValidationErrors}

        // Duplicate checking
        checkingRealtimeDuplicates={checkingRealtimeDuplicates}
        setCheckingRealtimeDuplicates={setCheckingRealtimeDuplicates}
        realtimeDuplicateWarnings={realtimeDuplicateWarnings}
        setRealtimeDuplicateWarnings={setRealtimeDuplicateWarnings}
        // Phase 3e.0 — submit-time duplicate-check loading indicator.
        checkingDuplicates={checkingDuplicates}

        // Change-request state — Phase 3b: native in V2.  The wizard
        // calls setChangeRequestSection(...) + setShowChangeRequestDialog(true)
        // when the user clicks "Request Change" for a section they can't
        // edit; we render ChangeRequestDialog ourselves below.
        // setChangeRequestProposedChanges / setChangeRequestFields are
        // legacy-only setters that the dialog used for a richer
        // multi-field flow that V2 doesn't support yet — safe to noop
        // because the actually-used path is the free-text reason.
        setShowChangeRequestDialog={setShowChangeRequestDialog}
        setChangeRequestProposedChanges={noop}
        setChangeRequestSection={setChangeRequestSection}
        setChangeRequestFields={noop}

        // Provider assignment — Phase 3c.2 (Feb 2026): native in V2.
        // The wizard fetches providers/services/enrollments internally
        // then mutates these states via the setters; we own the state
        // and render AssignProvidersDialog below.  setProviders is
        // noop because the static-data context already preloads
        // providers on app boot (no need to mutate from here).
        providers={providers}
        setProviders={setStaticDataProviders}
        setSelectedProvidersForAssignment={setSelectedProvidersForAssignment}
        setShowAssignProviders={setShowAssignProviders}
        setAvailableServices={setAvailableServices}
        setExistingEnrollments={setExistingEnrollments}
        setProviderServiceAssignments={setProviderServiceAssignments}

        // Client selection — Phase 3c.2 / March 2026 fix.
        //
        // The IntakeWizardDialog's "Assign Services" handler calls
        // `setSelectedClient(...)` with EITHER a real client doc, the
        // stub from `intakeClientId`, or the synthesized `{id:'new'}`
        // sentinel — all three previously mapped to `setEditingClient`
        // and broke the new-client flow (formData wipe + 404 on submit).
        //
        // Route ALL three through `selectedClientForAssign` instead,
        // and ONLY promote to `editingClient` when the AssignProviders
        // dialog itself re-fetches a real persisted client AFTER
        // creating enrollments (its `axios.get(/clients/{id}) →
        // setSelectedClient(response.data)` call on line ~596) — i.e.
        // when the incoming `next` has a real UUID id, not the
        // `'new'` sentinel, AND we were already in edit-existing mode
        // (i.e., the user had genuinely opened the wizard with a
        // clientId).  In every other case we just stash the value for
        // AssignProvidersDialog's own use; the wizard's editingClient
        // stays untouched.
        setSelectedClient={(next) => {
          setSelectedClientForAssign(next);
          // `safeSetEditingClient` (see contexts/useEditingClient.js)
          // refuses sentinels (`{id:'new'}`), stubs, and swaps to a
          // different client by construction.  Only a same-id refresh
          // on a client we were ALREADY editing passes the gate.
          safeSetEditingClient(next);
        }}
        setClientDetailTab={noop}
        setShowClientDetail={(open) => {
          if (open && editingClient?.id) {
            navigate(`/client/${editingClient.id}`);
            close();
          }
        }}

        // Reference data
        locations={locations}
        users={users}
        serviceTypes={serviceTypes}
        onViewChangeHistory={(client) => {
          const target = client || editingClient;
          if (target?.id) setChangeHistoryClient(target);
        }}

        // Handlers
        handleIntakeFieldChange={handleIntakeFieldChange}
        handleIntakeFieldValidation={handleIntakeFieldValidation}
        handleCloseIntakeWizard={close}
        handleSaveIntakeDraft={handleSaveIntakeDraft}
        handleGenerateIntakePDF={handleGenerateIntakePDF}
        handleCompleteIntake={handleCompleteIntake}
        handleOpenIntakeWizard={open}
        handleOpenInsuranceManagement={handleOpenInsuranceManagement}

        // Permission/config helpers — Phase 2 wires the real
        // helpers via `useIntakePermissions(intakeFormConfig)`.
        canEditFieldCategory={permissions.canEditFieldCategory}
        isFieldVisible={permissions.isFieldVisible}
        isFieldRequired={permissions.isFieldRequired}
        hasPermission={permissions.hasPermission}

        // Pending insurances
        pendingInsurances={pendingInsurances}
        setPendingInsurances={setPendingInsurances}

        // Medication dialog — Phase 3d.2: native in V2.  The wizard
        // sets editingMedication first (or null for "add new"), then
        // setShowMedicationForm(true); we mount AddEditMedicationDialog
        // below and sync results back to intakeData on save.
        setShowMedicationForm={setShowMedicationForm}
        setEditingMedication={setEditingMedication}
        clientMedications={editingClient?.medications || []}
      />

      {/* Phase 2 — render the DuplicateWarningDialog natively so the
          new-client flow doesn't bounce when potential matches are
          detected. */}
      <DuplicateWarningDialog
        open={showDuplicateWarning}
        onOpenChange={(open) => {
          if (!open) {
            setShowDuplicateWarning(false);
            setPendingClientData(null);
            setIntakePotentialDuplicates([]);
            setRevealedSSNs({});
          } else {
            setShowDuplicateWarning(true);
          }
        }}
        duplicates={intakePotentialDuplicates}
        revealedSSNs={revealedSSNs}
        setRevealedSSNs={setRevealedSSNs}
        isCreating={isCreatingClient}
        onCancel={() => {
          setShowDuplicateWarning(false);
          setPendingClientData(null);
          setIntakePotentialDuplicates([]);
          setRevealedSSNs({});
        }}
        onConfirmCreate={handleCreateClientAnyway}
        onUseExisting={async (dup) => {
          setShowDuplicateWarning(false);
          setPendingClientData(null);
          setIntakePotentialDuplicates([]);
          setRevealedSSNs({});
          // Pivot the wizard from "create new" into "edit the matched
          // existing client" — same UX as legacy.
          await open(dup.id);
          toast.info('Opened existing client for editing');
        }}
        onViewChart={(dup) => {
          setShowDuplicateWarning(false);
          setRevealedSSNs({});
          navigate(`/client/${dup.id}`);
          close();
        }}
      />

      {/* Phase 3b — render ChangeRequestDialog natively so the
          "Request Change" action inside IntakeWizard doesn't bounce
          to legacy.  The wizard sets `changeRequestSection` first
          then `showChangeRequestDialog=true`; we already wired its
          setters above. */}
      <ChangeRequestDialog
        open={showChangeRequestDialog}
        onOpenChange={setShowChangeRequestDialog}
        changeRequestSection={changeRequestSection}
        changeRequestReason={changeRequestReason}
        setChangeRequestReason={setChangeRequestReason}
        editingClient={editingClient}
      />

      {/* Phase 3c.2 — render AssignProvidersDialog natively.  In V2,
          `selectedClient` is the same as `editingClient` for the
          edit-existing flow; for new-client creation we synthesize a
          `{id: 'new', ...}` shape so the dialog's new-client branch
          activates and stages selections on intakeData. */}
      <AssignProvidersDialog
        open={showAssignProviders}
        onOpenChange={setShowAssignProviders}
        selectedClient={
          // Prefer the value the wizard set via the tracking shim above
          // (which may be a stub doc for new-client + Save-Draft flow),
          // then fall back to editingClient (real edit-existing flow),
          // then the synthesized 'new' sentinel.
          selectedClientForAssign || editingClient || {
            id: 'new',
            full_name: intakeData?.full_name || 'New Client',
            assigned_providers: intakeData?.assigned_providers || [],
          }
        }
        setSelectedClient={(next) => {
          // Same gate as the IntakeWizardDialog wiring above — see
          // contexts/useEditingClient.js for the rejection rules.
          setSelectedClientForAssign(next);
          safeSetEditingClient(next);
        }}
        editingClient={editingClient}
        providers={providers}
        users={users}
        serviceTypes={serviceTypes}
        availableServices={availableServices}
        existingEnrollments={existingEnrollments}
        setExistingEnrollments={setExistingEnrollments}
        selectedProvidersForAssignment={selectedProvidersForAssignment}
        setSelectedProvidersForAssignment={setSelectedProvidersForAssignment}
        providerServiceAssignments={providerServiceAssignments}
        setProviderServiceAssignments={setProviderServiceAssignments}
        resetSelections={() => {
          setSelectedProvidersForAssignment([]);
          setProviderServiceAssignments({});
          setAvailableServices([]);
          setExistingEnrollments([]);
        }}
        // The wizard sits open while this dialog is shown, so the
        // "new client" branch saves selections onto intakeData via
        // handleIntakeFieldChange.  clientFormData is only used when
        // the legacy simple-client form (not the wizard) is the
        // host — V2 never enters that branch, so noop is safe.
        showIntakeWizard={true}
        handleIntakeFieldChange={handleIntakeFieldChange}
        clientFormData={null}
        setClientFormData={noop}
        // V2 doesn't have a separate pending-enrollments store; the
        // selections are staged on intakeData and posted as part of
        // the final client-create cascade in `cascadeNewClientSideEffects`.
        setPendingServiceEnrollments={(pending) => {
          // Mirror onto intakeData so it survives the wizard's own
          // re-renders and gets posted alongside the new client.
          setIntakeData((prev) => ({ ...prev, pending_service_enrollments: pending }));
        }}
        // V2 doesn't own the legacy global client/enrollment caches.
        // Fire a window event so any open V2 route (Waitlist, etc.)
        // can re-fetch; safe to noop the App.js-specific cache APIs.
        setClientEnrollments={noop}
        invalidateEnrollmentCache={noop}
        enrollmentsCacheRef={null}
        refreshNotes={() => {
          try { window.dispatchEvent(new CustomEvent('v2:enrollments-changed')); } catch (_) { /* noop */ }
        }}
        refreshClients={noop}
        refreshStats={noop}
        // No custom confirm modal in V2; fall back to window.confirm
        // so the Approve-Disenrollment flow stays usable.
        confirmAction={(msg) => Promise.resolve(window.confirm(msg))}
      />

      {/* Phase 3d.2 — render AddEditMedicationDialog natively so the
          "Add/Edit Medication" action inside IntakeWizard doesn't
          bounce to legacy.  On success we refetch the client's
          medications, mirror them into editingClient + intakeData so
          the wizard's downstream reads stay live. */}
      <AddEditMedicationDialog
        open={showMedicationForm}
        onOpenChange={(next) => {
          setShowMedicationForm(next);
          if (!next) setEditingMedication(null);
        }}
        client={editingClient}
        editingMedication={editingMedication}
        onSuccess={async (clientId) => {
          if (!clientId) return;
          try {
            const medsRes = await axios.get(`${API}/clients/${clientId}/medications`);
            const meds = medsRes.data || [];
            // Mirror onto editingClient for the wizard's local read.
            setEditingClient((prev) => (
              prev && prev.id === clientId
                ? { ...prev, medications: meds }
                : prev
            ));
            // Mirror onto intakeData in the wizard's expected shape,
            // matching App.js:43833.
            const activeMeds = meds.filter((m) => m.is_active !== false);
            handleIntakeFieldChange('medications', activeMeds.map((m) => ({
              id: m.id,
              name: m.medication_name || m.name || '',
              dosage: m.dosage || '',
              frequency: m.frequency || '',
              prescriber: m.prescriber || '',
              is_active: true,
            })));
          } catch (err) {
            console.error('Failed to refresh medications after save:', err);
          }
        }}
      />

      {/* Feb 2026 — Native V2 Insurance Management.  Opened from
          IntakeWizardDialog → "Manage Insurance" or from any V2
          surface via `intakeWizard.handleOpenInsuranceManagement(client)`.
          Previously this bounced to /dashboard and the legacy
          dialog never opened (the bounce didn't trigger the
          legacy state-setters; the user just saw a toast and
          a route flicker).  Mounting here keeps users in V2. */}
      <ClientInsuranceDialog
        isOpen={showInsuranceManagement}
        onClose={() => {
          setShowInsuranceManagement(false);
          setManagingInsuranceForClient(null);
        }}
        client={managingInsuranceForClient}
        insuranceCompanies={insuranceCompanies || []}
        onUpdate={async (optimisticInsurances) => {
          // Jun 2026 — accept the optimistic snapshot from the dialog
          // so the IntakeWizard's insurance step reflects member-id /
          // priority edits instantly.  Authoritative refresh follows.
          if (!managingInsuranceForClient?.id) return;
          if (Array.isArray(optimisticInsurances)) {
            const optimisticPatch = { insurances: optimisticInsurances };
            setManagingInsuranceForClient((prev) => prev ? { ...prev, ...optimisticPatch } : prev);
            setEditingClient((prev) => (
              prev && prev.id === managingInsuranceForClient.id
                ? { ...prev, ...optimisticPatch }
                : prev
            ));
            return;
          }
          // Authoritative pass — fetch the canonical client doc so any
          // backend-only fields (audit timestamps, derived flags) land
          // alongside the insurance changes.
          try {
            const r = await axios.get(`${API}/clients/${managingInsuranceForClient.id}`);
            const fresh = r.data || null;
            if (fresh) {
              setManagingInsuranceForClient(fresh);
              setEditingClient((prev) => (
                prev && prev.id === fresh.id ? { ...prev, ...fresh } : prev
              ));
            }
          } catch (err) {
            console.warn('[IntakeWizardContext] post-insurance-update refetch failed:', err);
          }
        }}
      />
      <ChangeHistoryModal
        open={!!changeHistoryClient}
        onOpenChange={(o) => { if (!o) setChangeHistoryClient(null); }}
        entityType="client"
        entityId={changeHistoryClient?.id}
        entityName={changeHistoryClient?.full_name || 'Client'}
      />
    </IntakeWizardContext.Provider>
  );
}

export const useIntakeWizard = () => {
  const ctx = useContext(IntakeWizardContext);
  if (!ctx) {
    throw new Error('useIntakeWizard must be used within IntakeWizardProvider');
  }
  return ctx;
};

export default IntakeWizardContext;
